import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Catches render-time exceptions anywhere in the tree below it and shows
 * a friendly fallback with a "Reload" button instead of a blank white page.
 *
 * Also POSTs the error message + stack to /api/client-errors so we can
 * see crashes that only happen on a client device (e.g. iOS Chrome) which
 * we cannot reproduce locally.
 */
// DOM-manipulation errors thrown when a browser translator (Safari
// Translate / Google Translate) has moved React-managed nodes around.
// These are transient: re-rendering from scratch succeeds.
const TRANSLATION_ERROR_RE =
  /object can\s?not be found|NotFoundError|removeChild|insertBefore|not a child of this node/i;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '', stack: '', componentStack: '' };
    this.autoRecoveries = 0;
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || String(error) || 'Unexpected error',
      stack: error?.stack || '',
    };
  }

  componentDidCatch(error, info) {
    const componentStack = info?.componentStack || '';
    this.setState({ componentStack });

    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, componentStack);

    // Auto-recover from translator-induced DOM errors instead of showing
    // the full-screen crash page. Cap retries to avoid an error loop.
    const message = error?.message || String(error);
    const autoRecovered =
      TRANSLATION_ERROR_RE.test(message) && this.autoRecoveries < 3;
    if (autoRecovered) {
      this.autoRecoveries += 1;
      setTimeout(() => {
        this.setState({ hasError: false, message: '', stack: '', componentStack: '' });
      }, 0);
    }

    // Fire-and-forget report to backend. Never throw here — we are already
    // in the failure path.
    try {
      axios
        .post(
          `${API}/client-errors`,
          {
            message: error?.message || String(error),
            stack: error?.stack || null,
            component_stack: componentStack,
            user_agent:
              typeof navigator !== 'undefined' ? navigator.userAgent : null,
            url: typeof window !== 'undefined' ? window.location.href : null,
            extra: { auto_recovered: autoRecovered, recovery_attempt: this.autoRecoveries },
          },
          { timeout: 5000 }
        )
        .catch(() => {});
    } catch (_e) {
      // Already in the failure path — never rethrow, but leave a trace.
      // eslint-disable-next-line no-console
      console.warn('[ErrorBoundary] failed to report crash', _e);
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      window.location.href = window.location.href;
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center bg-[#17201a] text-[#f5f0e8] px-6 py-16"
      >
        <div className="max-w-md w-full text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#a84b2a] mb-4">
            Erreur · Error
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">
            Une erreur s'est produite
          </h2>
          <p className="font-light text-[#f5f0e8]/70 mb-6 leading-relaxed">
            Something went wrong on your device. Please reload the page to
            continue. Si le problème persiste, essayez de fermer puis rouvrir
            l'onglet.
          </p>

          {/* Show the actual error message on screen so a remote client
              can screenshot it. Helps us debug crashes we can't reproduce
              locally (typically iOS Chrome). */}
          <div
            className="text-left bg-black/30 border border-white/10 px-3 py-2 mb-6 font-mono text-[11px] leading-snug text-[#f5f0e8]/80 break-words"
            data-testid="error-boundary-message"
          >
            {this.state.message}
          </div>

          <button
            type="button"
            onClick={this.handleReload}
            className="btn-cta inline-flex items-center gap-2"
            data-testid="error-boundary-reload"
          >
            Reload · Recharger
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
